'use client';
import React, { useState } from 'react';
import { Lottie } from '@/components/LottieWrapper';
import { useLottieLoader } from '@/lib/useLottieLoader';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { PillButton } from '@/components/PillButton';

interface Tab {
  label: string;
  content: {
    title: string;
    text: string;
    image: {
      src: string;
      alt: string;
      width: number;
      height: number;
    };
    href?: string;
    lottie: object | string;
  };
}

interface TabComponentProps {
  tabs: Tab[];
}

export const Tab: React.FC<TabComponentProps> = ({ tabs }) => {
  const [activeTab, setActiveTab] = useState<number>(0);

  const handleTabClick = (index: number) => {
    setActiveTab(index);
  };

  const animationData = useLottieLoader(String(tabs[activeTab].content.lottie));

  return (
    <div>
      <ul className="my-8 flex gap-3 overflow-x-auto [scrollbar-width:none] md:mb-10 md:mt-12 lg:mb-8 lg:mt-14 xl:mb-6 xl:mt-16">
        {tabs.map((tab, index) => (
          <li key={`${tab.label}-${index}`}>
            <PillButton onClick={() => handleTabClick(index)} active={index === activeTab}>
              {tab.label}
            </PillButton>
          </li>
        ))}
      </ul>
      <div className="flex flex-col-reverse gap-6 rounded-[1rem] bg-light-background-200 px-6 py-8 md:gap-10 md:rounded-[2rem] md:px-10 md:py-10 lg:flex-row lg:gap-0 lg:px-16 lg:py-16 xl:px-20 xl:py-20">
        <div className="flex w-full flex-col justify-center pr-16 md:w-full lg:w-2/5 ">
          <h4 className="text-h5 mb-4 text-gray-1">{tabs[activeTab].content.title}</h4>
          <p className="text-body mb-10 text-gray-3">{tabs[activeTab].content.text}</p>

          {tabs[activeTab].content.href ? (
            <a
              href={tabs[activeTab].content.href}
              className="group inline-flex flex-col items-start rounded-lg border-gray-5 bg-white p-4"
            >
              <span className="flex items-center gap-1 capitalize text-gray-2">
                <span className="text-body underline-text-transition-blue flex justify-center">
                  Read Documentation
                  <ChevronRightIcon className="inline-block size-6 group-hover:translate-x-1" />
                </span>
              </span>
            </a>
          ) : null}
        </div>

        <div className="w-full overflow-hidden rounded-2xl bg-dark-background md:w-full lg:w-3/5">
          <Lottie
            animationData={animationData}
            autoplay={true}
            loop={false}
            className="m-0 h-full w-full md:p-6 lg:px-12 lg:py-3 xl:px-20"
          />
        </div>
      </div>
    </div>
  );
};
